// Mi marca — browser helpers: photos (scale down before sending, load for the
// canvas), PNG export at the exact size and copying text. Photos are only
// scaled (never up) and re-encoded: no filters, no retouching.
import { ownerCall } from '../shared.jsx';
import { BRAND_FORMATS, PHOTO_MAX_SIDE, PHOTO_MAX_BYTES, exportFileName, slideCountOf } from '../../../brandConfig.js';
import { layoutPiece, drawPiece } from '../../../brandRender.js';
import { hasTransparentEdge } from './brandExamples.js';

/**
 * The headline font of the collection (Montserrat, shipped with Oike). Every
 * drawing waits for it, so the catalog, the editor and the PNG measure the same
 * letters. If it cannot load, everything uses the same fallback (Arial Black).
 */
let fontsPromise = null;
export function brandFontsReady() {
  if (!fontsPromise) {
    fontsPromise = (typeof FontFace === 'undefined' || !document.fonts ? Promise.resolve() : Promise.all(['800', '900'].map(w =>
      new FontFace('Montserrat', `url(/fonts/montserrat/montserrat-latin-${w}-normal.woff2) format('woff2')`, { weight: w, style: 'normal' }).load()
        .then(f => document.fonts.add(f)).catch(() => null))))
      .then(() => { measureCtx = null; return true; });
  }
  return fontsPromise;
}

const cache = new Map(); // photoId → Promise<{ img, w, h }>

/** The photo as an image the canvas can use (through the server: Airtable's files cannot be read into a canvas). */
export function loadPhoto(id) {
  if (!cache.has(id)) {
    cache.set(id, ownerCall({ action: 'brandPhotoData', id }).then(({ dataUrl }) => new Promise((resolve, reject) => {
      if (!dataUrl) { reject(new Error('Sin imagen')); return; }
      const img = new Image();
      // A transparent background (a cut-out silhouette) is drawn as it is, with an optional halo
      img.onload = () => resolve({ img, w: img.naturalWidth, h: img.naturalHeight, src: dataUrl, cutout: /^data:image\/(png|webp)/.test(dataUrl) && hasTransparentEdge(img, img.naturalWidth, img.naturalHeight) });
      img.onerror = () => reject(new Error('No se pudo leer la imagen'));
      img.src = dataUrl;
    })).catch((e) => { cache.delete(id); throw e; }));
  }
  return cache.get(id);
}
export const forgetPhoto = (id) => cache.delete(id);

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('No se pudo leer el archivo'));
  r.readAsDataURL(blob);
});

/** A photo file, scaled down to PHOTO_MAX_SIDE at most (same proportions), ready to send. */
export async function preparePhoto(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error('Usa una foto JPG, PNG o WebP.');
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new Error('No se pudo abrir la foto.');
  const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale), h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  // A cut-out (transparent background) stays PNG, so its silhouette is kept; if it is
  // too heavy it is scaled down a bit more (never converted to JPEG, which would fill it)
  const cutout = file.type !== 'image/jpeg' && hasTransparentEdge(canvas, w, h);
  const keepPng = cutout || (file.type === 'image/png' && file.size <= PHOTO_MAX_BYTES && scale === 1);
  let blob = null, out = canvas;
  if (keepPng) {
    for (const k of [1, 0.85, 0.7, 0.55]) {
      if (k < 1) {
        out = document.createElement('canvas');
        out.width = Math.round(w * k); out.height = Math.round(h * k);
        out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
      }
      blob = await new Promise(r => out.toBlob(r, 'image/png'));
      if (blob && blob.size <= PHOTO_MAX_BYTES) break;
    }
  } else {
    for (const q of [0.9, 0.82, 0.72]) {
      blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', q));
      if (blob && blob.size <= PHOTO_MAX_BYTES) break;
    }
  }
  if (!blob || blob.size > PHOTO_MAX_BYTES) throw new Error('La foto es demasiado pesada, incluso reducida.');
  return { contentType: blob.type, data: await blobToBase64(blob), width: out.width, height: out.height, cutout, preview: canvas.toDataURL(cutout ? 'image/png' : 'image/jpeg', 0.6) };
}

/** Canvas text measure for the layout. */
let measureCtx = null;
export function measure(text, font) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

/** One PNG at the format's exact size (no overlays). */
export async function exportPng(piece, opts) {
  const fmt = BRAND_FORMATS[piece.format];
  const canvas = document.createElement('canvas');
  canvas.width = fmt.width; canvas.height = fmt.height;
  const layout = layoutPiece(piece, { ...opts, measure });
  drawPiece(canvas.getContext('2d'), layout, opts.images);
  const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
  if (!blob) throw new Error('No se pudo crear el PNG.');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = exportFileName(piece.title, piece.format, slideCountOf(piece) > 1 ? (opts.slide || 0) + 1 : null);
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { width: fmt.width, height: fmt.height, bytes: blob.size, name: a.download };
}

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    let ok = false;
    try { ok = document.execCommand('copy'); } catch { ok = false; }
    ta.remove();
    return ok;
  }
}

/**
 * A drawn example image for the catalog when Ale has no photo yet: a landscape at
 * dusk, marked "Imagen de ejemplo". Not a person and not a real photograph.
 */
let example = null;
export function exampleImage() {
  if (example) return example;
  const c = document.createElement('canvas');
  c.width = 1200; c.height = 1500;
  const x = c.getContext('2d');
  const sky = x.createLinearGradient(0, 0, 0, 1500);
  sky.addColorStop(0, '#E9C9A0'); sky.addColorStop(0.45, '#C9A27E'); sky.addColorStop(1, '#5E6B52');
  x.fillStyle = sky; x.fillRect(0, 0, 1200, 1500);
  const sun = x.createRadialGradient(780, 520, 10, 780, 520, 260);
  sun.addColorStop(0, 'rgba(255,240,205,0.95)'); sun.addColorStop(1, 'rgba(255,240,205,0)');
  x.fillStyle = sun; x.fillRect(0, 0, 1200, 1500);
  const hill = (y0, amp, color, seed) => {
    x.fillStyle = color; x.beginPath(); x.moveTo(0, 1500);
    for (let px = 0; px <= 1200; px += 20) x.lineTo(px, y0 + Math.sin(px / 170 + seed) * amp + Math.sin(px / 61 + seed * 2) * amp * 0.25);
    x.lineTo(1200, 1500); x.fill();
  };
  hill(820, 60, 'rgba(95,110,88,0.85)', 1); hill(960, 50, 'rgba(62,82,64,0.92)', 3); hill(1110, 40, 'rgba(38,58,46,0.97)', 5);
  // a little grain, so it does not look flat
  const g = x.getImageData(0, 0, 1200, 1500);
  for (let i = 0; i < g.data.length; i += 4) { const n = (Math.random() - 0.5) * 14; g.data[i] += n; g.data[i + 1] += n; g.data[i + 2] += n; }
  x.putImageData(g, 0, 0);
  x.fillStyle = 'rgba(255,255,255,0.85)'; x.font = '700 30px Arial'; x.fillText('IMAGEN DE EJEMPLO', 40, 1460);
  example = { img: c, w: 1200, h: 1500, src: c.toDataURL('image/jpeg', 0.8) };
  return example;
}
